import {
  IonButton,
  IonButtons,
  IonHeader,
  IonModal,
  IonToolbar,
} from '@ionic/react';
import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import {
  ExternalPayment,
  Payment as PaymentComponent,
} from '../components/payment';
import { analyticsHelper } from '../helpers';
import { webAnalyticsHelper } from '../helpers/webAnalytics';
import { Customer } from '../models';
import { customerResource } from '../resource';
import {
  DeviceContext,
  DeviceContextModel,
  TranslationContext,
  UserContext,
} from './index';
import './payment.scss';

interface PaymentContextModel {
  credits: number;
  minutesToUse: number;
  rechargeLink: string;
  refreshCredits: () => void;
  resetToppedUp: () => void;
  setCurrentCharacterShortCode: (shortCode: string | null) => void;
  setDisplayPaymentPopup: (value: boolean) => void;
  toppedUpBalance: boolean;
}

export const PaymentContext = createContext<PaymentContextModel>({
  credits: 0,
  minutesToUse: 0,
  refreshCredits: () => null,
  setCurrentCharacterShortCode: () => null,
  setDisplayPaymentPopup: () => null,
  toppedUpBalance: false,
  resetToppedUp: () => null,
  rechargeLink: '',
});

interface Props {
  children: ReactNode;
}

export const Payment: FC<Props> = ({ children }) => {
  const { isAuthenticated, isCustomer, user } = useContext(UserContext);
  const { isBrowser } = useContext(DeviceContext) as DeviceContextModel;
  const [credits, setCredits] = useState<number>(0);
  const [minutesToUse, setMinutesToUse] = useState<number>(0);
  const [toppedUpBalance, setToppedUpBalance] = useState<boolean>(false);
  const [displayPaymentPopup, setDisplayPaymentPopup] =
    useState<boolean>(false);
  const [rechargeLink, setRechargeLink] = useState<string>('');
  const [currentCharacterShortCode, setCurrentCharacterShortCode] = useState<
    string | null
  >(null);
  const { translate } = useContext(TranslationContext);
  const { pathname } = useLocation();

  const resetToppedUp = () => setToppedUpBalance(false);
  const closePopIn = () => setDisplayPaymentPopup(false);
  const onTopUp = async () => {
    await refreshCredits();
    setToppedUpBalance(true);
    setDisplayPaymentPopup(false);
  };

  /**
   * Gets user info from API about credits and minutes to use
   */
  const refreshCredits = useCallback(async () => {
    if (user) {
      const refreshedUser = await customerResource.find(
        (user as Customer)['@id'],
      );
      if (refreshedUser) {
        setCredits(refreshedUser.balance ?? 0);
        setMinutesToUse(refreshedUser.minutesToUse ?? 0);
      }
    }
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated || !isCustomer || !user) {
      setRechargeLink('');
      return;
    }

    let isMounted = true;
    const params = currentCharacterShortCode
      ? { characterShortCode: currentCharacterShortCode }
      : undefined;

    customerResource
      .find((user as Customer)['@id'], params)
      .then(refreshed => {
        if (!isMounted || !refreshed) {
          return;
        }
        const settings = refreshed.characterSettings ?? [];
        const link = currentCharacterShortCode
          ? settings.find(
              s => s.character.shortCode === currentCharacterShortCode,
            )?.rechargeLink
          : settings.find(s => s.rechargeLink)?.rechargeLink;
        setRechargeLink(link ?? '');
      })
      .catch(() => {
        if (isMounted) {
          setRechargeLink('');
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, isCustomer, user, currentCharacterShortCode]);

  /**
   * Refresh credits and minutes to use when user infos are updated
   */
  useEffect(() => {
    if (isAuthenticated && isCustomer && user) {
      setCredits((user as Customer).balance ?? 0);
      setMinutesToUse((user as Customer).minutesToUse ?? 0);
    }
  }, [isAuthenticated, isCustomer, user]);

  /**
   * Asks for credit refresh and reset status of "just add credits" when user go to experts list
   */
  useEffect(() => {
    if (pathname.startsWith('/customer/experts')) {
      refreshCredits();
      resetToppedUp();
    }
  }, [refreshCredits, pathname]);

  useEffect(() => {
    if (displayPaymentPopup) {
      analyticsHelper.logEvent('payment_open', {});
      webAnalyticsHelper.logEvent('payment_open', {});
    }
  }, [displayPaymentPopup]);

  return (
    <PaymentContext.Provider
      value={{
        credits,
        minutesToUse,
        rechargeLink,
        refreshCredits,
        setCurrentCharacterShortCode,
        setDisplayPaymentPopup,
        toppedUpBalance,
        resetToppedUp,
      }}
    >
      {children}

      {isAuthenticated && isCustomer ? (
        <IonModal
          backdropDismiss={true}
          className="payment-modal"
          isOpen={isCustomer && displayPaymentPopup}
        >
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="end">
                <IonButton onClick={closePopIn}>
                  {translate('payment.popup.close')}
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>

          {isBrowser ? (
            <ExternalPayment onClose={closePopIn} />
          ) : (
            <PaymentComponent onClose={closePopIn} onTopUp={onTopUp} />
          )}
        </IonModal>
      ) : null}
    </PaymentContext.Provider>
  );
};
